import { capitalize, isTopLevelEntityType } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import type { EntityType } from '@tunasong/schemas'
import { useMemo } from 'react'
import type { MouseEvent } from 'react'
import EntityAvatar from './entity-avatar.js'
import { List, ListItemAvatar, ListItemText, MenuItem, type ListProps } from '../primitives/index.js'

export interface EntityTypeListProps extends Omit<ListProps, 'onSelect' | 'selected'> {
  selected?: EntityType | null
  size?: 'small' | 'medium'
  onSelect?: (type: EntityType) => void
}

export const EntityTypeList = (props: EntityTypeListProps) => {
  const { selected, onSelect, size = 'medium', ...restProps } = props
  const plugins = usePlugins('all')
  const handleSelect = (type: EntityType) => (ev: MouseEvent) => {
    if (onSelect) {
      ev.preventDefault()
      onSelect(type)
    }
  }
  const topLevelPlugins = useMemo(
    () =>
      plugins
        ?.filter(p => p.node?.type && isTopLevelEntityType(p.node?.type as EntityType))
        .sort((a, b) => (a.node?.type ? a.node?.type?.localeCompare(b.node?.type ?? '') : 0)),
    [plugins]
  )

  return (
    <List {...restProps}>
      {topLevelPlugins
        ?.filter(p => p.node?.type)
        .map(plugin => {
          const type = plugin.node?.type as EntityType
          if (!type) {
            return null
          }
          return (
            <MenuItem key={plugin.node?.type} onClick={handleSelect(type)} selected={selected === type}>
              <ListItemAvatar>
                <EntityAvatar type={type as EntityType} />
              </ListItemAvatar>
              {size === 'medium' ? <ListItemText primary={capitalize(plugin.node?.type ?? 'Unknown')} /> : null}
            </MenuItem>
          )
        })}
    </List>
  )
}
